import {
  BaseMessageSignerWalletAdapter,
  MessageSignerWalletAdapter,
  SignerWalletAdapter,
  WalletAdapterNetwork
} from '@solana/wallet-adapter-base';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { CoinbaseWalletAdapter } from '@solana/wallet-adapter-coinbase';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { ChangeAccCbArgs, Connector } from '../interfaces/wallet.interface';
import { WalletConnectWalletAdapter } from '@solana/wallet-adapter-walletconnect';
import { BLOCKCHAINS, PROVIDERS } from '../providers';
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes';
import { ParentAppService } from '../../services/parrentApp.service';
import { solanaNetwork as network } from './network';
import {
  SolanaMobileWalletAdapter,
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
} from '@solana-mobile/wallet-adapter-mobile';
import { BackpackStandardAdapter } from './adapters/backpack';

const networkToChain = (n: WalletAdapterNetwork) => {
  switch (n) {
    case 'mainnet-beta':
      return 'solana:mainnet';
    case 'devnet':
      return 'solana:devnet';
    case 'testnet':
      return 'solana:testnet';
    default:
      return 'solana:mainnet';
  }
};

const Adapters = {
  [PROVIDERS.PHANTOM]: PROVIDERS.PHANTOM,
  [PROVIDERS.SOLFLARE]: PROVIDERS.SOLFLARE,
  [PROVIDERS.COINBASE]: PROVIDERS.COINBASE,
  [PROVIDERS.WALLET_CONNECT]: PROVIDERS.WALLET_CONNECT,
  [PROVIDERS.MOBILE]: PROVIDERS.MOBILE,
  [PROVIDERS.BACKPACK]: PROVIDERS.BACKPACK,
}

type AdapterMap = Partial<{
  [K in PROVIDERS]: BaseMessageSignerWalletAdapter;
}>;

const parentAppService = ParentAppService.getInstance();

const projectId = 'ed613db064bb88c7a349dee2e7112e21';
export class SolanaConnector implements Connector {
  private static instance: SolanaConnector;
  private adapters: AdapterMap = {
    [PROVIDERS.PHANTOM]: new PhantomWalletAdapter({ network }),
    [PROVIDERS.WALLET_CONNECT]: new WalletConnectWalletAdapter({
      network,
      options: {
          relayUrl: 'wss://relay.walletconnect.com',
          projectId,
          metadata: {
              name: 'Ultrade',
              description: 'Ultrade',
              url: 'ultrade.org',
              icons: ['https://avatars.githubusercontent.com/u/35608259?s=200'],
          },
      },
    }),

    // Android Chrome (web) — direct MWA.
    [PROVIDERS.MOBILE]: new SolanaMobileWalletAdapter({
      addressSelector: createDefaultAddressSelector(),
      appIdentity: {
        name: window.location.hostname,
        uri: window.location.origin,
      },
      authorizationResultCache: createDefaultAuthorizationResultCache(),
      chain: networkToChain(network),
      onWalletNotFound: createDefaultWalletNotFoundHandler(),
    }),
    [PROVIDERS.SOLFLARE]: new SolflareWalletAdapter({ network }),
    [PROVIDERS.COINBASE]: new CoinbaseWalletAdapter({ network }),
    [PROVIDERS.BACKPACK]: new BackpackStandardAdapter({ network }),
  } as const;

  public isConnected: boolean = false;
  public adapter: (BaseMessageSignerWalletAdapter & Partial<MessageSignerWalletAdapter & SignerWalletAdapter>) | null = null;
  public address: string;
  public provider: 'PHANTOM' | 'SOLFLARE' | 'MOBILE' | 'WALLET_CONNECT' | 'COINBASE' | 'BACKPACK' | null = null;

  private constructor() {}

  public static getInstance(): SolanaConnector {
    if (!SolanaConnector.instance) {
      SolanaConnector.instance = new SolanaConnector();
    }
    return SolanaConnector.instance;
  }

  public async connect(chain: BLOCKCHAINS, provider: PROVIDERS, reconnect: boolean = false) {
    if(!Adapters[provider]) {
      throw new Error(`You trying to connect solana with ${provider}.`);
    }
    if (parentAppService.checkIfAppEmbedded() && (
      provider === PROVIDERS.PHANTOM ||
      provider === PROVIDERS.SOLFLARE ||
      provider === PROVIDERS.MOBILE ||
      provider === PROVIDERS.BACKPACK
    )) {
      this.provider = provider;
      this.address = await parentAppService.connectSolana(provider, reconnect);
      this.isConnected = true;
      return chain;
    }
    const currentAdapter = this.adapters[provider];
    console.log('adapter ready state: ', currentAdapter.readyState);
    console.log('solana: ', currentAdapter.url);
    if(currentAdapter.readyState === 'NotDetected') {
      window.open(currentAdapter.url);
      throw new Error('Adapter is not detected');
    } else {
      await currentAdapter.connect();
      this.address = currentAdapter.publicKey?.toString();
      this.isConnected = true;
      this.adapter = currentAdapter;
      return chain;
    }
  }

  async switch(chain: BLOCKCHAINS, provider: PROVIDERS): Promise<void> {
    setTimeout(async () => {
      await this.disconnect();
      await this.connect(chain, provider);
    }, 1000); 
  }

  public async reconnect(chain: BLOCKCHAINS, provider: PROVIDERS) {
    await this.connect(chain, provider);
    return chain;
  }

  public async disconnect() {
    if (this.adapter) {
      await this.adapter.disconnect();
      this.isConnected = false;
      this.adapter = null;
    } else if (parentAppService.checkIfAppEmbedded()) {
      // await parentAppService.disconnectSolana();
      try { await parentAppService.disconnectSolana(); } catch {}
      this.provider = null;
    }
  }

  public setStateChangeCb(cb: (data: ChangeAccCbArgs) => void): () => void {
      return () => {};
  }

  public async signMessage(message: string, encoding?: BufferEncoding): Promise<string> {
    if (parentAppService.checkIfAppEmbedded() && !this.adapter) {
      const signature = await parentAppService.signMessageSolana(message, encoding);
      return signature;
    }
    if (!this.adapter || typeof (this.adapter as any).signMessage !== 'function') {
      throw new Error('Wallet does not support message signing');
    }        
    const signature = await this.adapter.signMessage(new Uint8Array(Buffer.from(message, encoding)));
    return bs58.encode(signature);
  }
}

export const checkSolanaWallet = () => {
  const anyWin = window as any;
  const solana = anyWin.solana;

  const phantomDetected =
    Boolean(anyWin.phantom?.solana) ||
    Boolean(solana && (solana.isPhantom || solana?.provider?.isPhantom));

  const solflareDetected =
    Boolean(anyWin.solflare?.isSolflare) ||
    Boolean(solana && (solana.isSolflare || solana?.provider?.isSolflare));

  const backpackDetected = Boolean(anyWin.backpack?.isBackpack);

  return { phantom: phantomDetected, solflare: solflareDetected, backpack: backpackDetected};
}
